import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, updateUser, user } = useAuth();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading'); // loading | success | failed | expired
  const [paymentData, setPaymentData] = useState(null);
  const attemptsRef = useRef(0);
  const maxAttempts = 8;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    pollStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStatus = async () => {
    if (attemptsRef.current >= maxAttempts) {
      setStatus('expired');
      return;
    }

    attemptsRef.current += 1;

    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/checkout-status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        if (res.status === 404) {
          setStatus('failed');
          return;
        }
        throw new Error('Status check failed');
      }

      const data = await res.json();
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user plan if subscription
        if (data.payment_type === 'subscription' && data.plan_id && updateUser && user) {
          updateUser({ ...user, plan_id: data.plan_id });
        }
        toast.success('Payment confirmed!');
        return;
      }

      if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Still pending — retry after 2 seconds
      setTimeout(pollStatus, 2000);
    } catch (err) {
      console.error('Poll error:', err);
      setTimeout(pollStatus, 2000);
    }
  };

  const getDestination = () => {
    if (!paymentData) return '/dashboard/settings?tab=billing';
    if (paymentData.payment_type === 'report') return '/dashboard/reports';
    return '/dashboard/settings?tab=billing';
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-6">

        {status === 'loading' && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-primary animate-spin" />
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-2">Confirming your payment…</h1>
              <p className="text-muted-foreground">
                Please wait while we verify your payment with Stripe.
              </p>
            </div>
            <div className="flex gap-1 justify-center">
              {[...Array(maxAttempts)].map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-6 rounded-full transition-colors ${
                    i < attemptsRef.current ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
              {paymentData?.payment_type === 'subscription' ? (
                <p className="text-muted-foreground">
                  Your account has been upgraded to{' '}
                  <span className="font-semibold capitalize text-foreground">
                    {paymentData.plan_id}
                  </span>
                  . Enjoy your new features!
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Your report purchase was successful. You can now access the full report.
                </p>
              )}
              {paymentData?.amount && (
                <p className="text-sm text-muted-foreground mt-1">
                  Amount charged: <span className="font-medium">${paymentData.amount.toFixed(2)} USD</span>
                </p>
              )}
            </div>
            <Button
              onClick={() => navigate(getDestination())}
              className="w-full gap-2"
              data-testid="payment-success-continue-btn"
            >
              {paymentData?.payment_type === 'report' ? 'Go to Reports' : 'View My Plan'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        )}

        {(status === 'failed' || status === 'expired') && (
          <>
            <div className="mx-auto w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-2">
                {status === 'expired' ? 'Payment Timed Out' : 'Payment Not Confirmed'}
              </h1>
              <p className="text-muted-foreground">
                {status === 'expired'
                  ? 'We could not confirm your payment in time. If you were charged, please contact support.'
                  : 'We could not verify your payment. Please try again or contact support if the issue persists.'}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/settings?tab=plans')}
                className="flex-1"
              >
                Back to Plans
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1"
                data-testid="payment-failed-dashboard-btn"
              >
                Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
